import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { ActionHandlerFunction, EmailGroup, IActionHandlerTyped, AdminUserBase, UserListData, UserProfile } from "@preveil-api";
import { Account, account_types, AdminErrorMessages, AdminUIActionTypes, isSameUser, Message, MessageAnchors, useAppDispatch, usePostUsersFindMutation, UserListActionTypes } from "src/common";
import { UserList } from "src/components";
import { MembersTable } from "..";
import { uiActions } from "src/store";
import _ from "lodash";

type AllProps = {
  current_account: Account
  handleAction: ActionHandlerFunction;
  is_update_mode?: boolean;
  selected_group?: EmailGroup;
  is_valid?: boolean;
  setIsValid: Dispatch<SetStateAction<boolean>>;
  org_users: AdminUserBase[];
  group_alias: string;
  setGroupAlias: Dispatch<SetStateAction<string>>;
  valid_group_users: string[];
  invalid_alias?: boolean;
}

// Description: Component use to enter individual addresses/domains for User Groups
function IndividualFormComponent(props: AllProps) {
  const { handleAction, is_update_mode, current_account, selected_group, is_valid, setIsValid, org_users, group_alias, setGroupAlias, valid_group_users, invalid_alias } = props;
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string>("");
  const [full_users, setFullUsers] = useState<AdminUserBase[]>();
  const [group_account_users_data, setAccountUsersData] = useState<AdminUserBase[]>([]);
  const user_list_ref = useRef<{ handlePageActions: ActionHandlerFunction }>();
  const removed_users_list_ref = useRef<Map<string, string>>(new Map);
  const [findUsers] = usePostUsersFindMutation();

  useEffect(() => {
    const _valid = group_alias.length > 0 && (!_.isEmpty(valid_group_users) || group_account_users_data.length >= 1) && !invalid_alias;
    setIsValid(_valid);
  }, [group_alias, valid_group_users, group_account_users_data, invalid_alias]);

  useEffect(() => {
    if (!!org_users && org_users.length > 0) {
      const _full_users = _.filter(org_users, ((_user: AdminUserBase) =>
        _user.account_type !== account_types.no_user && !_user.deleted));
      setFullUsers(_full_users);
    }
  }, [org_users]);

  function handleSubmitGroup() {
    if (is_update_mode && !!selected_group) {
      const member_ids = selected_group.members.map((member: AdminUserBase) => member.user_id);
      const removed_addresses = member_ids.filter((member: string) => removed_users_list_ref.current.has(member.toLowerCase()));
      const added_addresses = group_account_users_data.filter((user_account: AdminUserBase) => !member_ids.includes(user_account.user_id)).map((added_accounts) => added_accounts.user_id);
      const params = {
        add: added_addresses.length >= 1 ? added_addresses : [],
        remove: removed_addresses.length >= 1 ? removed_addresses : [],
        alias: group_alias
      };
      setGroupAlias("");
      handleAction({ actionType: AdminUIActionTypes.Update, params });
    } else {
      validateGroupEmail();
    }
  }

  function reset() {
    !is_update_mode && setGroupAlias("");
    const params = is_update_mode ? selected_group?.members.map((member: AdminUserBase) => member.user_id) : [];
    if (!!user_list_ref.current) {
      user_list_ref.current.handlePageActions({
        actionType: UserListActionTypes.Reset, params
      });
    }
  }

  // Description: Before creating the user group email, this function validates that the alias provided 
  // by the user is not an actual preveil users id or is not an already existing user group email
  async function validateGroupEmail() {
    return await findUsers({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: { spec: [{ user_id: group_alias, key_version: -1 }] }
    })
      .unwrap()
      .then(({ users, groups }) => {
        let err: string | undefined;
        if (!!users.find((user) => user.user_id === group_alias)) {
          err = AdminErrorMessages.group_email_alias_error.replace(MessageAnchors.user_id, `User '${group_alias}'`);
        } else if (!!groups.find((group) => group.alias === group_alias)) {
          err = AdminErrorMessages.group_email_alias_error.replace(MessageAnchors.user_id, `Email Group '${group_alias}'`);
        }
        if (!!err) {
          dispatch(uiActions.handleRequestErrors(new Message(err)));
          setGroupAlias("");
        } else {
          const members = group_account_users_data.map((account) => account.user_id);
          handleAction({
            actionType: AdminUIActionTypes.Create,
            params: { alias: group_alias, members },
          });
          setGroupAlias("");
        }
      })
      .catch((msg: string) => {
        dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_creating_group_email), msg));
        setIsValid(false);
      });
  }

  function handleShowModal() {
    handleAction({ actionType: AdminUIActionTypes.ShowModal });
    setGroupAlias("");
  }

  // Description: Add users to the list on select
  function handleUpdateUsers(action_obj: IActionHandlerTyped<UserListData>) {
    const params = action_obj?.params;
    const { current_contacts } = params;
    const members: AdminUserBase[] = [];
    _.forEach(current_contacts, (contact: UserProfile) => {
      const address = contact?.address;
      // Note: In some cases when the account was created in V1 using uppercase letters,
      // the user_id will be returned with the mixed-case/uppercase letters (full_users array)
      // causing issues with case sensitivity in V2 (ex: in this case user won't be found).
      // so this is why we are using isSameUser to compare the user_id.
      const user = full_users?.find((user: AdminUserBase) => isSameUser(user.user_id, address));
      if (!!user) {
        const user_details = {
          ...user,
          user_id: user.user_id.toLowerCase()
        };
        members.push(user_details);
        setError("");
      } else {
        setError(AdminErrorMessages.error_unable_add_user.replace(MessageAnchors.user_id, address));
      }
      if (removed_users_list_ref && removed_users_list_ref?.current.has(address)) {
        removed_users_list_ref.current.delete(address);
      }
    });
    setAccountUsersData(members);
  }

  function handleRemoveUser(user: string[]) {
    if (!!user_list_ref.current) {
      if (is_update_mode) {
        handleAdminGroupUpdates(user[0], "remove");
      }
      const org_user = org_users.find((org_user: AdminUserBase) => isSameUser(org_user.user_id, user[0]));
      if (org_user) {
        const user_profile: UserProfile = { address: org_user.user_id, name: org_user.display_name, role: org_user.entity_role };
        user_list_ref.current.handlePageActions({
          actionType: UserListActionTypes.RemoveProfile,
          params: user_profile,
        });
      }
    }
  }

  function handleAdminGroupUpdates(key: string, action: "add" | "remove") {
    if (action === "remove") {
      removed_users_list_ref.current.set(key, key);
    } else if (action === "add" && removed_users_list_ref.current?.has(key)) {
      removed_users_list_ref.current.delete(key);
    }
  }

  function hasGroupChanged(): boolean {
    if (!is_update_mode) return true;
    const initial_group_users: string[] = is_update_mode && !!selected_group ? selected_group?.members.map((member: AdminUserBase) => member.user_id) : [];
    const group_ids = group_account_users_data.map((user: AdminUserBase) => user.user_id);
    return !_.isEqual(_.sortBy(initial_group_users), _.sortBy(group_ids));
  }

  return <>
    <h5>Add Users</h5>
    <p className="mb-1">Please add one or more people to your group list.</p>
    <UserList
      placeholder="Start typing to add a user"
      ref={user_list_ref}
      current_account={current_account}
      handleAction={handleUpdateUsers}
      hide_badges={true}
      org_users={full_users}
      user_ids={!!selected_group ? selected_group.members.map((member: AdminUserBase) => member.user_id) : []}
      show_external_users={false}
    />
    {!!error && <small className="error">{error}</small>}
    {group_account_users_data.length > 0 && (
      <MembersTable members={group_account_users_data} removeUser={handleRemoveUser}></MembersTable>
    )}
    <div className="btn-panel mt-4">
      <Button type="submit" onClick={handleSubmitGroup} disabled={!is_valid || !hasGroupChanged()}>
        {`${!!is_update_mode ? "Update" : "Create"} User Group`}</Button>
      <Button onClick={reset} disabled={(!is_update_mode && group_alias.length === 0) || !hasGroupChanged()} variant="no-outline-primary">Reset</Button>
      <Button onClick={handleShowModal} variant="no-outline-primary">Cancel</Button>
    </div>
  </>;
}

export default React.memo(IndividualFormComponent);
